import React, { useState, useEffect } from 'react';
import MuiGrid from '../components/MuiGrid';
import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GavelIcon from '@mui/icons-material/Gavel';
import type { Player } from '../types/models';

const Players: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    // Filter players based on search term
    if (searchTerm.trim() === '') {
      setFilteredPlayers(players);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = players.filter(
        player => 
          player.nickname.toLowerCase().includes(term) || 
          player.full_name.toLowerCase().includes(term)
      );
      setFilteredPlayers(filtered);
    }
  }, [searchTerm, players]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      // Fetch all players
      const { data, error } = await supabase
        .from('players')
        .select(`
          *,
          results:game_results(
            is_win
          )
        `)
        .order('nickname', { ascending: true });

      if (error) throw error;

      // Calculate win counts for each player
      const playersWithStats = data?.map(player => {
        const winCount = player.results?.filter((r: any) => r.is_win).length || 0;
        const gameCount = player.results?.length || 0;
        
        return {
          ...player,
          winCount,
          gameCount
        };
      }) || [];

      setPlayers(playersWithStats);
      setFilteredPlayers(playersWithStats);
    } catch (error: any) {
      console.error('Error fetching players:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Players
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search players by name..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Player</TableCell>
                    {!isMobile && <TableCell>Full Name</TableCell>}
                    <TableCell align="center">Games</TableCell>
                    <TableCell align="center">Wins</TableCell>
                    <TableCell align="center">Win Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPlayers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No players found matching your search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPlayers.map((player) => (
                      <TableRow
                        key={player.id}
                        hover
                        onClick={() => navigate(`/player/${player.id}`)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={player.profile_picture_url || undefined}
                              sx={{ 
                                width: 40, 
                                height: 40, 
                                bgcolor: player.is_judge ? 'secondary.main' : 'primary.main',
                                mr: 2
                              }}
                            >
                              {getInitials(player.nickname)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {player.nickname}
                              </Typography>
                              {isMobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {player.full_name}
                                </Typography>
                              )}
                              {player.is_judge && (
                                <Chip 
                                  icon={<GavelIcon />}
                                  label="Judge" 
                                  size="small" 
                                  color="secondary" 
                                  sx={{ ml: 1, height: 24 }} 
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        
                        {!isMobile && (
                          <TableCell>
                            {player.full_name}
                          </TableCell>
                        )}
                        
                        <TableCell align="center">
                          <Typography variant="body1" fontWeight="medium">
                            {(player as any).gameCount || 0}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Typography variant="body1" fontWeight="medium" color="success.main">
                            {(player as any).winCount || 0}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Chip
                            label={
                              (player as any).gameCount > 0 
                                ? ((((player as any).winCount || 0) / (player as any).gameCount) * 100).toFixed(0) + '%' 
                                : '0%'
                            }
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Players;